import {
  AMBER, CONTENT_TOP, FONT, INK, MUTED, NAVY, SLIDE_H, SLIDE_W,
  downloadCanvasPng, ellipsize, fitText, loadSlideBranding, roundRect, slideFileName, startSlide,
} from './slideCanvas';

// ── Team Categories slide ───────────────────────────────────────────────────
// A 1920×1080 PNG of the tour's teams — name, age group, tour leader (shown
// as "Coach") and numbered players — sized to drop straight into the Canva
// welcome presentation as a full slide.

function fullName(p) {
  return [p.firstName, p.surname].filter(Boolean).join(' ') || p.email || '';
}

/**
 * The slide content: [{ name, ageLabel, coach, players: [name] }], one per
 * team in Teams-tab order. Players are whoever is placed in the team.
 */
export function teamCategoriesData(teams, assignments, participants) {
  const byId = Object.fromEntries((participants || []).map((p) => [p.id, p]));
  return (teams || []).map((team) => ({
    name: team.name || 'Team',
    ageLabel: team.ageLabel || '',
    coach: team.tourLeaderName || '',
    players: (assignments?.[team.id] || []).map((pid) => byId[pid]).filter(Boolean).map(fullName).filter(Boolean),
  }));
}

/** Rows × columns for n cards: one row up to 4 teams, then two (or more) rows. */
export function cardGrid(n) {
  if (n <= 4) return { cols: Math.max(n, 1), rows: 1 };
  const rows = Math.ceil(n / 5);
  return { cols: Math.ceil(n / rows), rows };
}

function drawCard(ctx, team, x, y, w, h) {
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.28)';
  ctx.shadowBlur = 24;
  ctx.shadowOffsetY = 8;
  ctx.fillStyle = '#FFFFFF';
  roundRect(ctx, x, y, w, h, 18);
  ctx.fill();
  ctx.restore();

  // Header band
  const headH = 112;
  ctx.save();
  roundRect(ctx, x, y, w, h, 18);
  ctx.clip();
  ctx.fillStyle = NAVY;
  ctx.fillRect(x, y, w, headH);
  ctx.fillStyle = AMBER;
  ctx.fillRect(x, y + headH, w, 6);
  ctx.restore();

  const pad = 26;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.fillStyle = '#FFFFFF';
  fitText(ctx, team.name.toUpperCase(), w - pad * 2, 900, 38, 20);
  ctx.fillText(ellipsize(ctx, team.name.toUpperCase(), w - pad * 2), x + pad, y + 52);

  if (team.ageLabel) {
    ctx.font = `700 20px ${FONT}`;
    const label = ellipsize(ctx, team.ageLabel, w - pad * 2 - 24);
    const pillW = ctx.measureText(label).width + 24;
    ctx.fillStyle = AMBER;
    roundRect(ctx, x + pad, y + 66, pillW, 32, 16);
    ctx.fill();
    ctx.fillStyle = NAVY;
    ctx.fillText(label, x + pad + 12, y + 89);
  }

  // Coach line
  let cy = y + headH + 6 + 44;
  ctx.font = `800 15px ${FONT}`;
  ctx.fillStyle = MUTED;
  ctx.fillText('COACH', x + pad, cy - 2);
  const coachLabelW = ctx.measureText('COACH').width + 12;
  ctx.font = `700 22px ${FONT}`;
  ctx.fillStyle = INK;
  ctx.fillText(ellipsize(ctx, team.coach || '—', w - pad * 2 - coachLabelW), x + pad + coachLabelW, cy);
  cy += 22;
  ctx.fillStyle = '#E5E7EB';
  ctx.fillRect(x + pad, cy, w - pad * 2, 2);
  cy += 18;

  // Players — one column, or two when the card is wide and the list is long
  const players = team.players;
  if (!players.length) {
    ctx.font = `italic 500 20px ${FONT}`;
    ctx.fillStyle = MUTED;
    ctx.fillText('Players to be confirmed', x + pad, cy + 28);
    return;
  }
  const listH = y + h - pad - cy;
  const lineAt = (n) => listH / n;
  let cols = 1;
  if (lineAt(players.length) < 30 && w >= 480) cols = 2;
  const perCol = Math.ceil(players.length / cols);
  const lineH = Math.min(46, lineAt(perCol));
  const size = Math.max(13, Math.min(24, Math.floor(lineH * 0.58)));
  const colW = (w - pad * 2) / cols;
  const numW = size * 1.9;
  players.forEach((name, i) => {
    const col = Math.floor(i / perCol);
    const row = i % perCol;
    const px = x + pad + col * colW;
    const py = cy + row * lineH + lineH * 0.7;
    ctx.font = `800 ${size}px ${FONT}`;
    ctx.fillStyle = AMBER;
    ctx.textAlign = 'right';
    ctx.fillText(String(i + 1), px + numW - 10, py);
    ctx.textAlign = 'left';
    ctx.font = `600 ${size}px ${FONT}`;
    ctx.fillStyle = INK;
    ctx.fillText(ellipsize(ctx, name, colW - numW - 8), px + numW, py);
  });
}

/** Draws the slide onto `canvas` (resized to 1920×1080). */
export async function drawTeamCategories(canvas, data, opts = {}) {
  const ctx = await startSlide(canvas, 'TEAM CATEGORIES', opts);

  // Team cards
  const { cols, rows } = cardGrid(data.length);
  const top = CONTENT_TOP;
  const side = 72;
  const gap = 36;
  const cardW = Math.min(520, (SLIDE_W - side * 2 - gap * (cols - 1)) / cols);
  const cardH = (SLIDE_H - top - 64 - gap * (rows - 1)) / rows;
  data.forEach((team, i) => {
    const row = Math.floor(i / cols);
    const inRow = Math.min(cols, data.length - row * cols);
    const rowW = inRow * cardW + (inRow - 1) * gap;
    const x = (SLIDE_W - rowW) / 2 + (i % cols) * (cardW + gap);
    const y = top + row * (cardH + gap);
    drawCard(ctx, team, x, y, cardW, cardH);
  });
}

/** Builds the slide for a tour and downloads it as a PNG. */
export async function downloadTeamCategoriesPng({ request, teams, assignments, participants }) {
  const data = teamCategoriesData(teams, assignments, participants);
  const { tourName, logoSrc, crestSrc } = await loadSlideBranding(request);
  const canvas = document.createElement('canvas');
  await drawTeamCategories(canvas, data, { subtitle: tourName, logoSrc, crestSrc });
  await downloadCanvasPng(canvas, slideFileName(tourName, 'Team_Categories'));
}
